import { useSyncExternalStore } from 'react'

import { WS_DEBUG_ENABLED } from '@lib/constants'

/**
 * Runtime-toggleable dev diagnostics. Availability is gated by `WS_DEBUG_ENABLED`
 * (env opt-in, never production). When available, individual tools are toggled at
 * runtime from the WS debug panel and persisted in localStorage.
 */
export type DebugSettings = {
  /** Per-table / per-row / per-cell render-count badges. */
  renderCounters: boolean
  /** WS activity panel open state — persisted so the panel survives page reloads. */
  wsPanelOpen: boolean
  /** Last dragged WS panel position (viewport px); null = default corner. */
  wsPanelPos: { x: number; y: number } | null
}

const STORAGE_KEY = 'admin-debug-settings'

const DEFAULT_DEBUG_SETTINGS: DebugSettings = {
  renderCounters: false,
  wsPanelOpen: false,
  wsPanelPos: null,
}

const readInitial = (): DebugSettings => {
  if (!WS_DEBUG_ENABLED) return DEFAULT_DEBUG_SETTINGS
  try {
    const raw = typeof window !== 'undefined' && window.localStorage.getItem(STORAGE_KEY)

    return raw ? { ...DEFAULT_DEBUG_SETTINGS, ...JSON.parse(raw) } : DEFAULT_DEBUG_SETTINGS
  } catch {
    return DEFAULT_DEBUG_SETTINGS
  }
}

let settings = readInitial()
const listeners = new Set<() => void>()

const getDebugSettings = (): DebugSettings => settings

export const setDebugSettings = (partial: Partial<DebugSettings>): void => {
  settings = { ...settings, ...partial }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
  } catch {
    /* ignore SSR */
  }
  listeners.forEach((l) => l())
}

const subscribeDebugSettings = (listener: () => void): (() => void) => {
  listeners.add(listener)

  return () => {
    listeners.delete(listener)
  }
}

export const useDebugSettings = () =>
  useSyncExternalStore(subscribeDebugSettings, getDebugSettings, () => DEFAULT_DEBUG_SETTINGS)
